import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

function buildAppUser(authUser, profile) {
  if (!authUser) return null;
  const fullName = profile?.full_name ?? authUser.user_metadata?.full_name ?? '';
  return {
    id: authUser.id,
    email: authUser.email ?? '',
    full_name: fullName,
    role: profile?.role ?? null,
    voucher_code: profile?.voucher_code ?? null,
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfileAndSyncUser = useCallback(async (session) => {
    if (!session?.user) {
      setUser(null);
      setProfile(null);
      setRole(null);
      return;
    }

    try {
      const { data: profileRow, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();

      if (error) throw error;

      setProfile(profileRow ?? null);
      setRole(profileRow?.role ?? null);
      setUser(buildAppUser(session.user, profileRow ?? null));
    } catch (err) {
      console.error('Failed to load profile', err);
      setUser(buildAppUser(session.user, null));
      setProfile(null);
      setRole(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      setLoading(true);
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (cancelled) return;
        await loadProfileAndSyncUser(data?.session ?? null);
      } catch (err) {
        console.error('Failed to get session', err);
        if (!cancelled) {
          setUser(null);
          setProfile(null);
          setRole(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    boot();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (cancelled) return;

        // Boot already ran getSession + profile; avoid flipping global loading on these
        // or the whole app (ProtectedRoute / public routes) spams spinners on refresh & refresh-token.
        if (_event === 'INITIAL_SESSION' || _event === 'TOKEN_REFRESHED') {
          try {
            await loadProfileAndSyncUser(session);
          } catch (err) {
            console.error('Auth state change handler failed', err);
          }
          return;
        }

        setLoading(true);
        try {
          if (_event === 'SIGNED_OUT') {
            setUser(null);
            setProfile(null);
            setRole(null);
            return;
          }

          await loadProfileAndSyncUser(session);
        } catch (err) {
          console.error('Auth state change handler failed', err);
        } finally {
          if (!cancelled) setLoading(false);
        }
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [loadProfileAndSyncUser]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    const { data: profileRow } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', data.user.id)
      .maybeSingle();
    const appUser = buildAppUser(data.user, profileRow);
    setProfile(profileRow);
    setRole(profileRow?.role ?? null);
    setUser(appUser);
    return appUser;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setRole(null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, role, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
